import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { getMyProjects } from "../services/projectService";
import { getProjectReport } from "../services/reportService";

import ReportLayout from "../components/reports/ReportLayout";
import ProjectOverviewCard from "../components/reports/ProjectOverviewCard";
import TaskSummaryCard from "../components/reports/TaskSummaryCard";
import MilestoneSummaryCard from "../components/reports/MilestoneSummaryCard";
import PhaseBreakdownTable from "../components/reports/PhaseBreakdownTable";
import ReportSkeleton from "../components/reports/ReportSkeleton";
import EmptyReportState from "../components/reports/EmptyReportState";

/* ===================================================== */
/* REPORTS PAGE                                          */
/* ===================================================== */

export default function ReportsPage() {
  const [searchParams] = useSearchParams();

  /* ================= STATE ================= */

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [error, setError] = useState("");

  const requestRef = useRef(0);

  /* ===================================================== */
  /* LOAD PROJECTS                                         */
  /* ===================================================== */

  useEffect(() => {
    const loadProjects = async () => {
      setLoadingProjects(true);
      setError("");

      try {
        const list = await getMyProjects();
        const arr = Array.isArray(list) ? list : [];

        setProjects(arr);

        const fromUrl = searchParams.get("projectId");
        const fromLS = localStorage.getItem("lastReportProjectId");

        const initial =
          (fromUrl && arr.find((p) => p._id === fromUrl)?._id) ||
          (fromLS && arr.find((p) => p._id === fromLS)?._id) ||
          arr[0]?._id ||
          "";

        setProjectId(initial);

        if (initial) {
          localStorage.setItem("lastReportProjectId", initial);
        }
      } catch (e) {
        setError("Failed to load projects");
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [searchParams]);

  /* ===================================================== */
  /* LOAD REPORT                                           */
  /* ===================================================== */

  useEffect(() => {
    if (!projectId) {
      setReport(null);
      return;
    }

    const currentRequest = ++requestRef.current;

    const loadReport = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await getProjectReport(projectId);

        // prevent race condition
        if (requestRef.current !== currentRequest) return;

        setReport(data);
        localStorage.setItem("lastReportProjectId", projectId);
      } catch (e) {
        if (requestRef.current !== currentRequest) return;

        setError("Failed to load report");
        setReport(null);
      } finally {
        if (requestRef.current === currentRequest) {
          setLoading(false);
        }
      }
    };

    loadReport();
  }, [projectId]);

  /* ===================================================== */
  /* HANDLERS                                              */
  /* ===================================================== */

  const handleRetry = () => {
    if (projectId) {
      setProjectId((prev) => prev); // trigger reload
    }
  };

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

 return (
  <ReportLayout>
    <div className="space-y-6">

      {/* ================================================= */}
      {/* PAGE HEADER                                        */}
      {/* ================================================= */}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Reports
          </h1>

          <p className="mt-1 text-sm text-gray-500">
            Analyze project progress, milestones, completion rates and team performance.
          </p>
        </div>

        {!loadingProjects && projects.length > 0 && (
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-600">
              Project
            </label>

            <select
              value={projectId}
              disabled={loading}
              onChange={(e) => setProjectId(e.target.value)}
              className="
                min-w-[320px]
                rounded-xl
                border
                border-gray-300
                bg-white
                px-4
                py-2.5
                text-sm
                shadow-sm
                disabled:opacity-50
              "
            >
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ================================================= */}
      {/* PROJECT LOADING                                   */}
      {/* ================================================= */}

      {loadingProjects && (
        <div className="rounded-2xl border bg-white p-6">
          <div className="animate-pulse text-gray-500">
            Loading projects...
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* NO PROJECTS                                       */}
      {/* ================================================= */}

      {!loadingProjects && projects.length === 0 && (
        <div className="rounded-2xl border bg-white p-10 text-center">
          <h3 className="text-lg font-semibold">
            No Projects Found
          </h3>

          <p className="mt-2 text-sm text-gray-500">
            Create your first project to start tracking analytics and progress.
          </p>
        </div>
      )}

      {/* ================================================= */}
      {/* REPORT LOADING                                    */}
      {/* ================================================= */}

      {loading && (
        <div className="space-y-6">
          <ReportSkeleton />
        </div>
      )}

      {/* ================================================= */}
      {/* EMPTY STATE                                       */}
      {/* ================================================= */}

      {!loading && !projectId && (
        <div className="rounded-2xl border bg-white p-10">
          <EmptyReportState />
        </div>
      )}

      {/* ================================================= */}
      {/* REPORT DATA                                       */}
      {/* ================================================= */}

      {!loading && projectId && report && (
        <div className="space-y-6">

          {/* PROJECT HEADER */}

          <div className="rounded-2xl border bg-white p-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">

              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {report.project?.name}
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  Project performance overview and delivery metrics
                </p>
              </div>

              <div className="text-sm text-gray-500">
                Last Updated{" "}
                {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* OVERVIEW */}

          <ProjectOverviewCard project={report.project} />

          {/* KPI GRID */}

          <div className="grid gap-6 lg:grid-cols-2">
            <TaskSummaryCard tasks={report.tasks} />
            <MilestoneSummaryCard milestones={report.milestones} />
          </div>

          {/* PHASE BREAKDOWN */}

          <div className="rounded-2xl border bg-white p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Phase Breakdown
              </h3>

              <p className="text-sm text-gray-500">
                Progress and task distribution by phase.
              </p>
            </div>

            <PhaseBreakdownTable
              phases={report.tasks?.byPhase || []}
            />
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* ERROR                                             */}
      {/* ================================================= */}

      {!loading && error && (
        <div
          className="
            rounded-2xl
            border
            border-red-200
            bg-red-50
            p-5
            flex
            items-center
            justify-between
          "
        >
          <div>
            <h4 className="font-medium text-red-700">
              Failed to load report
            </h4>

            <p className="text-sm text-red-600 mt-1">
              {error}
            </p>
          </div>

          <button
            onClick={handleRetry}
            className="
              rounded-lg
              bg-red-600
              px-4
              py-2
              text-white
              hover:bg-red-700
            "
          >
            Retry
          </button>
        </div>
      )}

    </div>
  </ReportLayout>
);
}